import {
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DISPLAY_DATE_TIME_FORMAT } from 'src/constants/date';
import {
  DtoolsProjectItem,
  useGetDtoolsProjects,
} from 'src/graphql/entities/integrations/dtools/hooks/use-get-dtools-projects';
import { ProjectsListProps } from './import-proposals.types';

const PAGE_SIZE = 10;

export const DtoolsProjectsList = ({
  filterText,
  onProjectsSelected,
  showSelectAll,
  initialSelectedIds,
}: ProjectsListProps<DtoolsProjectItem>) => {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const {
    projects,
    loading,
    fetchMore,
    total = 0,
  } = useGetDtoolsProjects({
    pageSize: PAGE_SIZE,
    pageNumber: 1,
    searchValue: filterText,
  });
  const [selectedAll, setSelectedAll] = useState<boolean>(false);
  const [selectedProjects, setSelectedProjects] = useState<DtoolsProjectItem[]>([]);

  const onProposalClickHandler = useCallback(
    (project: DtoolsProjectItem) => () => {
      const nextValue = selectedProjects.some(({ id }) => id === project.id)
        ? selectedProjects.filter((p) => p.id !== project.id)
        : selectedProjects.concat(project);
      setSelectedProjects(nextValue);
      onProjectsSelected?.(nextValue);
      setSelectedAll(nextValue.length > 0 && nextValue.length === projects?.length);
    },
    [onProjectsSelected, projects?.length, selectedProjects],
  );

  const onLoadMoreHandler = useCallback(async () => {
    const nextPage = currentPage + 1;
    setFetchMoreLoading(true);
    await fetchMore({
      variables: {
        params: {
          searchValue: filterText,
          pageSize: PAGE_SIZE,
          pageNumber: nextPage,
        },
      },
    });
    setFetchMoreLoading(false);
    setCurrentPage(nextPage);
  }, [currentPage, fetchMore, filterText]);

  const onSelectAll = useCallback(() => {
    if (projects === undefined) {
      return;
    }
    const nextState = !selectedAll;
    const nextProjects = nextState ? projects : [];
    setSelectedAll(nextState);
    setSelectedProjects(nextProjects);
    onProjectsSelected?.(nextProjects);
  }, [onProjectsSelected, projects, selectedAll]);

  useEffect(() => {
    if (filterText?.length) {
      setCurrentPage(1);
    }
  }, [filterText]);

  useEffect(() => {
    setSelectedProjects((projects || [])
      .filter(({ id }) => initialSelectedIds?.includes(id)));
    setSelectedAll((initialSelectedIds?.length ?? 0) > 0 && initialSelectedIds?.length === projects?.length);
  // Intentionally incomplete set of dependencies to properly handle initialSelected values on first render only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const listItems = useMemo(
    () =>
      (projects || [])
        .map((p) => {
        const labelId = `checkbox-list-label-${p.id}`;
        const selected = selectedProjects.some(({ id }) => id === p.id);
        return (
          <ListItem key={p.id} dense disableGutters disablePadding>
            <ListItemButton
              role={undefined}
              onClick={onProposalClickHandler(p)}
              disableRipple
              sx={{
                backgroundColor: selected ? theme.palette.action.hover : undefined,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: theme.spacing(4),
                }}
              >
                <Checkbox
                  edge="start"
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  slotProps={{
                    input: {
                      'aria-labelledby': labelId,
                    },
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={p.name}
                secondary={`#${p.number} - ${p.clientName ?? ''} - ${dayjs(p.modifiedDate ?? p.createdDate).format(DISPLAY_DATE_TIME_FORMAT)}`}
              />
            </ListItemButton>
          </ListItem>
        );
      }),
    [onProposalClickHandler, projects, selectedProjects, theme],
  );

  const selectAllOption = useMemo(() => {
    if (!showSelectAll) {
      return null;
    }
    return (
      <ListItem key="select-all" dense disableGutters disablePadding>
        <ListItemButton role={undefined} onClick={onSelectAll} disableRipple>
          <ListItemIcon
            sx={{
              minWidth: theme.spacing(4),
            }}
          >
            <Checkbox
              edge="start"
              checked={selectedAll}
              tabIndex={-1}
              disableRipple
              size="small"
              indeterminate={
                projects?.length !== selectedProjects.length && selectedProjects.length > 0
              }
              slotProps={{
                input: {
                  'aria-labelledby': 'select-all-option',
                },
              }}
            />
          </ListItemIcon>
          <ListItemText id="select-all-option" primary="Select all visible" />
        </ListItemButton>
      </ListItem>
    );
  }, [
    onSelectAll,
    projects?.length,
    selectedAll,
    selectedProjects.length,
    showSelectAll,
    theme,
  ]);

  if (loading) {
    return (
      <List>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
      </List>
    );
  }

  return (
    <List dense>
      {selectAllOption}
      {listItems}
      {listItems.length < total && listItems.length > 0 ? (
        <ListItem dense disableGutters disablePadding>
          <ListItemButton
            sx={{
              paddingY: theme.spacing(),
              justifyContent: 'center',
            }}
            onClick={onLoadMoreHandler}
            disableRipple
            disabled={fetchMoreLoading}
          >
            {fetchMoreLoading ? (
              <ListItemIcon>
                <CircularProgress size={20} />
              </ListItemIcon>
            ) : (
              <Typography variant="body2">Load More...</Typography>
            )}
          </ListItemButton>
        </ListItem>
      ) : null}
    </List>
  );
};
